<template>
  <div class="customer-wallet-transactions">
    <div class="transactions-header">
      <h2>Historique des transactions</h2>

      <div class="flex justify-between items-center gap-2">
        <!-- Filtres de type -->
        <div class="transactions-filters">
          <button
            :class="['filter-btn', { active: typeFilter === 'all' }]"
            @click="setTypeFilter('all')"
          >
            Toutes
          </button>
          <button
            :class="['filter-btn credit', { active: typeFilter === 'credit' }]"
            @click="setTypeFilter('credit')"
          >
            <i class="fa-solid fa-arrow-down"></i>
            Crédits
          </button>
          <button
            :class="['filter-btn debit', { active: typeFilter === 'debit' }]"
            @click="setTypeFilter('debit')"
          >
            <i class="fa-solid fa-arrow-up"></i>
            Débits
          </button>
          <button
            :class="['filter-btn refund', { active: typeFilter === 'refund' }]"
            @click="setTypeFilter('refund')"
          >
            <i class="fa-solid fa-rotate-left"></i>
            Remboursements
          </button>
        </div>

        <!-- Filtres de date -->
        <div class="transactions-date-filters">
          <div class="date-filter-group">
            <label>Du</label>
            <input type="date" v-model="dateFrom" @change="handleDateFilterChange" />
          </div>
          <div class="date-filter-group">
            <label>Au</label>
            <input type="date" v-model="dateTo" @change="handleDateFilterChange" />
          </div>
          <button v-if="dateFrom || dateTo" @click="clearDateFilters" class="clear-date-btn">
            <i class="fa-solid fa-xmark"></i>
            Réinitialiser
          </button>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="transactions-loading">
      <div class="spinner-small"></div>
      <p>Chargement...</p>
    </div>

    <!-- Empty -->
    <div v-else-if="transactions.length === 0" class="no-transactions">
      <i class="fa-solid fa-wallet"></i>
      <h3>Aucune transaction {{ getTypeFilterLabel() }}</h3>
      <p>Les transactions du portefeuille apparaîtront ici</p>
    </div>

    <!-- Tableau -->
    <div v-else class="transactions-table-container">
      <table class="transactions-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Montant</th>
            <th>Solde après</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="transaction in transactions" :key="transaction.id">
            <td class="td-date">
              {{ formatDateTime(transaction.created_at) }}
            </td>
            <td class="td-type">
              <span :class="['type-badge', transaction.type]">
                <i :class="getTypeIcon(transaction.type)"></i>
                {{ getTypeLabel(transaction.type) }}
              </span>
            </td>
            <td class="td-amount" :class="getAmountClass(transaction.type)">
              {{ getAmountPrefix(transaction.type) }}{{ formatCurrency(transaction.amount) }}
            </td>
            <td class="td-balance">
              {{ formatCurrency(transaction.balance_after) }}
            </td>
            <td class="td-description">
              {{ transaction.description || '-' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <PaginationComponent
      v-if="!loading && transactions.length > 0"
      :current-page="currentPage"
      :per-page="perPage"
      :total="total"
      :to="paginationTo"
      :from="paginationFrom"
      @page-change="goToPage"
    />
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue';
import customerWalletTransactionService from '@/services/customerWalletTransactionService';
import PaginationComponent from '@/components/common/PaginationComponent.vue';

export default defineComponent({
  name: 'CustomerWalletTransactions',
  components: {
    PaginationComponent
  },
  props: {
    walletId: {
      type: [Number, String],
      required: true
    }
  },
  setup(props) {
    const transactions = ref([]);
    const loading = ref(false);
    const typeFilter = ref('all');
    const dateFrom = ref('');
    const dateTo = ref('');

    // Pagination
    const currentPage = ref(1);
    const perPage = ref(10);
    const total = ref(0);
    const paginationFrom = ref(0);
    const paginationTo = ref(0);

    const loadTransactions = async () => {
      if (!props.walletId) return;

      try {
        loading.value = true;

        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          sort_by: 'created_at',
          sort_direction: 'desc'
        };

        if (typeFilter.value !== 'all') {
          params.type = typeFilter.value;
        }

        if (dateFrom.value) {
          params.start_date = dateFrom.value;
        }
        if (dateTo.value) {
          params.end_date = dateTo.value;
        }

        const response = await customerWalletTransactionService.getWalletTransactionsByWallet(
          props.walletId,
          params
        );

        if (response.data.data) {
          transactions.value = response.data.data || [];
        } else {
          transactions.value = [];
        }

        if (response.data.meta) {
          const meta = response.data.meta;
          total.value = meta.total || 0;
          perPage.value = meta.per_page || 10;
          paginationFrom.value = meta.from || 0;
          paginationTo.value = meta.to || 0;
        }
      } catch (error) {
        console.error('Erreur lors du chargement des transactions:', error);
        transactions.value = [];
      } finally {
        loading.value = false;
      }
    };

    const setTypeFilter = (filter) => {
      typeFilter.value = filter;
      currentPage.value = 1;
      loadTransactions();
    };

    const handleDateFilterChange = () => {
      currentPage.value = 1;
      loadTransactions();
    };

    const clearDateFilters = () => {
      dateFrom.value = '';
      dateTo.value = '';
      currentPage.value = 1;
      loadTransactions();
    };

    const goToPage = (page) => {
      if (page < 1) return;
      currentPage.value = page;
      loadTransactions();
    };

    const getTypeFilterLabel = () => {
      const labels = {
        'all': '',
        'credit': 'de type crédit',
        'debit': 'de type débit',
        'refund': 'de type remboursement'
      };
      return labels[typeFilter.value] || '';
    };

    const getTypeLabel = (type) => {
      const labels = {
        'credit': 'Crédit',
        'debit': 'Débit',
        'refund': 'Remboursement'
      };
      return labels[type] || type;
    };

    const getTypeIcon = (type) => {
      const icons = {
        'credit': 'fa-solid fa-arrow-down',
        'debit': 'fa-solid fa-arrow-up',
        'refund': 'fa-solid fa-rotate-left'
      };
      return icons[type] || 'fa-solid fa-circle';
    };

    const getAmountClass = (type) => {
      return {
        'amount-credit': type === 'credit' || type === 'refund',
        'amount-debit': type === 'debit'
      };
    };

    const getAmountPrefix = (type) => {
      return type === 'debit' ? '-' : '+';
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0);
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    watch(() => props.walletId, (newVal) => {
      if (newVal) {
        currentPage.value = 1;
        loadTransactions();
      }
    });

    onMounted(() => {
      if (props.walletId) {
        loadTransactions();
      }
    });

    return {
      transactions,
      loading,
      typeFilter,
      dateFrom,
      dateTo,
      currentPage,
      perPage,
      total,
      paginationFrom,
      paginationTo,
      setTypeFilter,
      handleDateFilterChange,
      clearDateFilters,
      goToPage,
      getTypeFilterLabel,
      getTypeLabel,
      getTypeIcon,
      getAmountClass,
      getAmountPrefix,
      formatCurrency,
      formatDateTime,
      loadTransactions
    };
  }
});
</script>

<style scoped>
.customer-wallet-transactions {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: fit-content;
}

.transactions-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.transactions-header h2 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.transactions-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: #f9fafb;
}

.filter-btn.active {
  background: #0A5C6B;
  color: white;
  border-color: #0A5C6B;
}

.filter-btn.credit.active {
  background: #059669;
  border-color: #059669;
}

.filter-btn.debit.active {
  background: #dc2626;
  border-color: #dc2626;
}

.filter-btn.refund.active {
  background: #7c3aed;
  border-color: #7c3aed;
}

.transactions-date-filters {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.date-filter-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-filter-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

.date-filter-group input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  color: #111827;
  transition: all 0.2s;
}

.date-filter-group input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.clear-date-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-date-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #111827;
}

.transactions-loading,
.no-transactions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: #6b7280;
}

.no-transactions i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

.no-transactions h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem;
}

.no-transactions p {
  font-size: 0.875rem;
  margin: 0;
}

/* Table styles */
.transactions-table-container {
  overflow-x: auto;
}

.transactions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.transactions-table thead {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.transactions-table th {
  padding: 0.875rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.transactions-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s;
}

.transactions-table tbody tr:hover {
  background: #f8fafc;
}

.transactions-table td {
  padding: 1rem;
  color: #334155;
  vertical-align: middle;
}

.td-date {
  color: #64748b;
  white-space: nowrap;
}

.td-type {
  white-space: nowrap;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.type-badge.credit {
  background: #d1fae5;
  color: #059669;
}

.type-badge.debit {
  background: #fee2e2;
  color: #dc2626;
}

.type-badge.refund {
  background: #ede9fe;
  color: #7c3aed;
}

.td-amount {
  font-weight: 600;
  white-space: nowrap;
}

.td-amount.amount-credit {
  color: #059669;
}

.td-amount.amount-debit {
  color: #dc2626;
}

.td-balance {
  font-weight: 500;
  color: #0f172a;
  white-space: nowrap;
}

.td-description {
  color: #64748b;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(10, 92, 107, 0.3);
  border-top-color: #0A5C6B;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .transactions-header {
    flex-direction: column;
    gap: 1rem;
  }

  .transactions-filters {
    flex-wrap: wrap;
  }

  .transactions-date-filters {
    flex-direction: column;
    align-items: flex-start;
  }

  .transactions-table th,
  .transactions-table td {
    padding: 0.75rem 0.5rem;
  }

  .td-description {
    max-width: 150px;
  }
}
</style>
